// passkey.ts — U65 (locker-pwa-passkey Phase 2.5)
// 패스키 등록·인증·관리 API 함수.
// registerPasskeyFlow / authenticatePasskeyFlow 는 브라우저 WebAuthn 흐름과
// 서버 API 왕복을 하나의 async 함수로 통합한다.

import {
  registerPasskey,
  authenticatePasskey,
} from '../utils/webauthn';
import { api } from './client';

// ─── 타입 정의 ───────────────────────────────────────────────

/** 패스키 목록 항목 요약 (서버 응답 스키마) */
export interface PasskeySummary {
  id: number;
  /** 사용자가 지정한 기기 이름 (예: "iPhone 15 Pro") */
  deviceLabel: string;
  /** 등록 시각 (Unix timestamp ms) */
  createdAt: number;
  /** 마지막 사용 시각 (null = 미사용) */
  lastUsedAt: number | null;
  /** 인증기 전송 방식 목록 (예: ["internal", "hybrid"]) */
  transports: string[] | null;
}

/** passkeyApi.list 응답 */
export interface PasskeyListResponse {
  items: PasskeySummary[];
}

/** 등록 완료 응답 */
export interface PasskeyRegisterFinishResponse {
  id: number;
  credentialId: string;
}

/** 인증 완료 응답 */
export interface PasskeyAuthFinishResponse {
  unlocked: true;
}

// ─── 원자 API 함수 ────────────────────────────────────────────

/**
 * 패스키 원자 API 모음.
 * 각 함수는 서버 엔드포인트 1개를 1:1 래핑한다.
 * 브라우저 WebAuthn 조작 없이 순수 HTTP만 수행.
 */
export const passkeyApi = {
  // ── 등록 ──────────────────────────────────────────────────

  /**
   * 등록 시작 — 서버에서 PublicKeyCredentialCreationOptionsJSON을 받는다.
   * 반환값을 registerPasskey(utils/webauthn)에 전달한다.
   */
  registerBegin: () => api.post<unknown>('/api/auth/passkey/register/begin'),

  /**
   * 등록 완료 — 브라우저가 생성한 등록 응답 + deviceLabel을 서버에 전달한다.
   * 성공 시 신규 passkey { id, credentialId } 반환.
   */
  registerFinish: (
    response: unknown,
    deviceLabel: string,
  ): Promise<PasskeyRegisterFinishResponse> =>
    api.post<PasskeyRegisterFinishResponse>('/api/auth/passkey/register/finish', {
      response,
      deviceLabel,
    }),

  // ── 인증 ──────────────────────────────────────────────────

  /**
   * 인증 시작 — 서버에서 PublicKeyCredentialRequestOptionsJSON을 받는다.
   * 반환값을 authenticatePasskey(utils/webauthn)에 전달한다.
   */
  authBegin: () => api.post<unknown>('/api/auth/passkey/auth/begin'),

  /**
   * 인증 완료 — 브라우저가 생성한 인증 응답을 서버에 전달한다.
   * 성공 시 { unlocked: true } 반환.
   */
  authFinish: (response: unknown): Promise<PasskeyAuthFinishResponse> =>
    api.post<PasskeyAuthFinishResponse>('/api/auth/passkey/auth/finish', { response }),

  // ── 관리 ──────────────────────────────────────────────────

  /**
   * 등록된 패스키 목록 조회.
   */
  list: (): Promise<PasskeyListResponse> =>
    api.get<PasskeyListResponse>('/api/auth/passkey'),

  /**
   * 패스키 이름 변경.
   * @param id - 변경할 passkey ID
   * @param label - 새 기기 이름
   */
  rename: (id: number, label: string): Promise<PasskeySummary> =>
    api.patch<PasskeySummary>(`/api/auth/passkey/${id}`, { label }),

  /**
   * 패스키 삭제.
   * @param id - 삭제할 passkey ID
   */
  remove: (id: number): Promise<void> =>
    api.del<void>(`/api/auth/passkey/${id}`),
};

// ─── 통합 흐름 ───────────────────────────────────────────────

/**
 * 패스키 등록 통합 흐름 (사용자 입장에서 1번 호출).
 *
 * 1. registerBegin → 서버에서 등록 옵션 수신
 * 2. registerPasskey(options) → 브라우저/기기 인증기 프롬프트 → 등록 응답 생성
 * 3. registerFinish(response, deviceLabel) → 서버에 응답 제출 → 완료
 *
 * @param deviceLabel - 기기 이름 (예: "MacBook Pro Touch ID")
 * @returns 서버가 생성한 passkey { id, credentialId }
 * @throws {WebauthnNotSupportedError} WebAuthn 미지원 환경
 * @throws {WebauthnUserCancelledError} 사용자 취소
 * @throws {WebauthnDeviceError} 인증기 오류
 * @throws {ApiError} 서버 오류
 */
export async function registerPasskeyFlow(
  deviceLabel: string,
): Promise<PasskeyRegisterFinishResponse> {
  // 단계 1: 서버에서 등록 옵션 획득
  const options = await passkeyApi.registerBegin();
  // 단계 2: 브라우저 WebAuthn API로 인증기 등록 응답 생성
  const response = await registerPasskey(
    options as Parameters<typeof registerPasskey>[0],
  );
  // 단계 3: 서버에 등록 응답 제출
  return passkeyApi.registerFinish(response, deviceLabel);
}

/**
 * 패스키 인증 통합 흐름 (사용자 입장에서 1번 호출).
 *
 * 1. authBegin → 서버에서 인증 옵션 수신
 * 2. authenticatePasskey(options) → 브라우저/기기 인증기 프롬프트 → 인증 응답 생성
 * 3. authFinish(response) → 서버에 응답 제출 → 잠금 해제
 *
 * @returns { unlocked: true }
 * @throws {WebauthnNotSupportedError} WebAuthn 미지원 환경
 * @throws {WebauthnUserCancelledError} 사용자 취소
 * @throws {WebauthnDeviceError} 인증기 오류
 * @throws {ApiError} 서버 오류
 */
export async function authenticatePasskeyFlow(): Promise<PasskeyAuthFinishResponse> {
  // 단계 1: 서버에서 인증 옵션 획득
  const options = await passkeyApi.authBegin();
  // 단계 2: 브라우저 WebAuthn API로 인증기 인증 응답 생성
  const response = await authenticatePasskey(
    options as Parameters<typeof authenticatePasskey>[0],
  );
  // 단계 3: 서버에 인증 응답 제출
  return passkeyApi.authFinish(response);
}
